import {Box, Occupancy} from "@variocube/app-api";
import {ArrayUtils} from "@variocube/app-common";
import * as React from "react";
import {useCallback, useEffect} from "react";
import {auditLog} from "./auditLog";
import {BoxAccess, usePlatform, usePreventDeactivate} from "@variocube/cube-ui";
import {PoboxOccupancyContent} from "./model";
import {logger} from "./logger";
import {useLocalization} from "../i18n";
import {soundMachine} from "@variocube/app-host/src/SoundMachine";
import {getSoundByKey} from "../accessibility";

const log = logger.sub("PickupView");

type PickupViewProps = {
    assignedLocks: string[];
    occupancies: Occupancy[];
    boxes: Box[];
    preventReopen?: boolean;
    onDone: () => void;
}

export function PickupView({assignedLocks, boxes, occupancies, preventReopen, onDone}: PickupViewProps) {
    const platform = usePlatform();
    const {t, language} = useLocalization();

    usePreventDeactivate();

    log.info("Pickup view", {assignedLocks, occupancies});

    useEffect(() => {
        soundMachine.play(getSoundByKey(boxes.length == 1 ? 'SpeakPickupItemsFromBox' : 'SpeakPickupItemsFromBoxes', language));
    }, [t, language, boxes]);

    const handleConfirm = useCallback(async () => {
        onDone();
        for (const occupancy of occupancies) {
            log.info("Ending occupancy", occupancy);
            try {
                await auditLog(platform, "Pickup", {occupancy});
                await platform.endOccupancy(occupancy.uuid, {
                    gracePeriod: 180,
                });
            } catch (error) {
                log.error("Error while ending occupancy", error);
            }
        }

        for (const assignedLock of assignedLocks) {
            await auditLog(platform, "Pickup", {
                assignedLock
            });
        }
        soundMachine.play(getSoundByKey('SpeakPickupConfirmed', language));
    }, [onDone, platform, occupancies, t, language]);

    return (
        <BoxAccess
            boxes={boxes}
            platform={platform}
            strings={t}
            onCancel={onDone}
            onConfirm={handleConfirm}
            confirmTitle={t("PickupConfirmTitle")}
            timeout
            autoConfirm={preventReopen ? 1 : 20}
            {...getStrings(assignedLocks, occupancies, boxes)}
        />
    );
}

function getStrings(assignedLocks: string[], occupancies: Occupancy[], boxes: Box[]) {
    const hasAssignedLocks = ArrayUtils.hasItems(assignedLocks);

    const boxNames = boxes.map((b) => b.number).join(", ");

    for (const occupancy of occupancies) {
        const content = occupancy.content as PoboxOccupancyContent;
        if (content.delivery) {
            if (hasAssignedLocks) {
                return getDeliveryAndMailStrings(boxNames);
            }
            else if (boxes.length > 1) {
                return getMultipleDeliveriesStrings(boxNames);
            }
            else {
                return getSingleDeliveryStrings(boxNames);
            }
        }
        if (content.dispatch) {
            return getDispatchStrings(boxNames);
        }
        if (content.transfer) {
            return getTransferStrings(boxNames);
        }
        if (content.maintenance) {
            return getMaintenanceStrings(boxNames);
        }
    }

    if (hasAssignedLocks) {
        return getMailStrings(boxNames);
    }

    // for other cases we might have missed, transfer strings fit the best
    return getTransferStrings(boxNames);
}

function getDispatchStrings(boxNames: string) {
    const {t} = useLocalization();
    // for dispatch it's okay to always use plural
    return getStringsMultiple(t("PickupSubjectDispatchTitle"), t("PickupSubjectDispatchMessage"), boxNames);
}

function getSingleDeliveryStrings(boxNames: string) {
    const {t} = useLocalization();
    // single delivery
    return getStringsSingle(t("PickupSubjectDeliveryTitle"), t("PickupSubjectDeliveryMessage"), boxNames);
}

function getMultipleDeliveriesStrings(boxNames: string) {
    const {t} = useLocalization();
    // multiple deliveries, so plural
    return getStringsMultiple(t("PickupSubjectDeliveryTitle"), t("PickupSubjectDeliveryMessage"), boxNames);
}

function getDeliveryAndMailStrings(boxNames: string) {
    const {t} = useLocalization();
    // delivery and mail, always plural
    return getStringsMultiple(t("PickupSubjectDeliveryAndMailTitle"), t("PickupSubjectDeliveryAndMailMessage"), boxNames);
}

function getMailStrings(boxNames: string) {
    const {t} = useLocalization();
    return getStringsSingle(t("PickupSubjectMailTitle"), t("PickupSubjectMailMessage"), boxNames);
}

function getMaintenanceStrings(boxNames: string) {
    const {t} = useLocalization();
    // maintenance has completely different messages, always plural
    return {
        boxOpenTitle: t("PickupMaintenanceTitle"),
        boxOpenMessage: t("PickupMaintenanceMessage", { boxNames }),
        confirmMessage: t("PickupConfirmMaintenanceMessage", { boxNames }),
    };
}

function getTransferStrings(boxNames: string) {
    const {t} = useLocalization();
    // for transfer it can only be a single box
    return getStringsSingle(t("PickupSubjectTransferTitle"), t("PickupSubjectTransferMessage"), boxNames);
}

function getStringsMultiple(subjectTitle: string, subjectMessage: string, boxNames: string) {
    const {t} = useLocalization();
    return {
        boxOpenTitle: t("PickupTitle", {subject: subjectTitle}),
        boxOpenMessage: t("PickupMessageMultiple", {subject: subjectMessage, boxNames}),
        confirmMessage: t("PickupConfirmMessageMultiple", {subject: subjectMessage, boxNames})
    };
}

function getStringsSingle(subjectTitle: string, subjectMessage: string, boxNames: string) {
    const {t} = useLocalization();
    return {
        boxOpenTitle: t("PickupTitle", {subject: subjectTitle}),
        boxOpenMessage: t("PickupMessageSingle", {subject: subjectMessage, boxNames}),
        confirmMessage: t("PickupConfirmMessageSingle", {subject: subjectMessage, boxNames})
    };
}
